import type { UserCapture } from "@/stores/useCaptures";
import type { Decision } from "@/stores/useDecisions";
import type { ActionItem } from "@/stores/useActions";
import type { Assumption } from "@/stores/useAssumptions";
import type { Transcript } from "@/stores/useTranscripts";
import type { Upload } from "@/stores/useAssetLocker";
import type { Finding } from "@/stores/useFindings";
import type { Opportunity } from "@/stores/useOpportunities";
import type { Recommendation } from "@/stores/useRecommendations";
import type { FacilitationNote } from "@/stores/useFacilitationNotes";
import { stripNs, TAG_NS_OPPORTUNITY } from "@/lib/taxonomy";
import { matchesTags } from "@/lib/tags";

/**
 * Client-side search over a workshop's captured content. R1's answer to the
 * "make them searchable" non-negotiable (NN5) — a pure in-memory substring
 * filter, no backend. Extended (CC-SEARCH) to cover the Track A registers
 * (findings / opportunities / recommendations / notes), to match on tags,
 * to honour an optional active-tag facet, and to search across all 8
 * workshops (`searchAllWorkshops`). The per-category `→ {title, subtitle}`
 * projection is factored out into exported helpers (CC-LINKS step 0) so
 * search, linkage, and the insight library render an arbitrary record
 * identically. The semantic / RAG search via the BFF is the deploy-gated
 * upgrade (`searchDocs`); this gives a real search verb today without it.
 */

export type SearchCategory =
  | "capture"
  | "decision"
  | "action"
  | "assumption"
  | "transcript"
  | "upload"
  | "finding"
  | "opportunity"
  | "recommendation"
  | "note";

export type SearchHit = {
  category: SearchCategory;
  id: string;
  /** Primary line shown in the result row. */
  title: string;
  /** Optional secondary line (rationale / owner / caption). */
  subtitle?: string;
};

export type SearchSources = {
  captures: UserCapture[];
  decisions: Decision[];
  actions: ActionItem[];
  assumptions: Assumption[];
  transcripts: Transcript[];
  uploads: Upload[];
  findings: Finding[];
  opportunities: Opportunity[];
  recommendations: Recommendation[];
  notes: FacilitationNote[];
};

export type SearchResults = {
  captures: SearchHit[];
  decisions: SearchHit[];
  actions: SearchHit[];
  assumptions: SearchHit[];
  transcripts: SearchHit[];
  uploads: SearchHit[];
  findings: SearchHit[];
  opportunities: SearchHit[];
  recommendations: SearchHit[];
  notes: SearchHit[];
  total: number;
};

const EMPTY: SearchResults = {
  captures: [],
  decisions: [],
  actions: [],
  assumptions: [],
  transcripts: [],
  uploads: [],
  findings: [],
  opportunities: [],
  recommendations: [],
  notes: [],
  total: 0,
};

/** The ordered list of categories — drives result iteration + facet counts. */
export const SEARCH_CATEGORIES: SearchCategory[] = [
  "capture",
  "decision",
  "action",
  "assumption",
  "transcript",
  "upload",
  "finding",
  "opportunity",
  "recommendation",
  "note",
];

function snippet(text: string, n = 120): string {
  return text.length > n ? `${text.slice(0, n).trim()}…` : text;
}

/* ── Per-category projection (the shared "render any record as a row")
 *    — exported so linkage + the insight library reuse the exact same row
 *    shape search produces. ──────────────────────────────────────────── */

export const projectCapture = (c: UserCapture): SearchHit => ({
  category: "capture",
  id: c.id,
  title: c.title,
  subtitle: c.caption || c.activity || undefined,
});
export const projectDecision = (d: Decision): SearchHit => ({
  category: "decision",
  id: d.id,
  title: d.title,
  subtitle: d.rationale || d.owner || undefined,
});
export const projectAction = (a: ActionItem): SearchHit => ({
  category: "action",
  id: a.id,
  title: a.title,
  subtitle: [a.owner, a.due].filter(Boolean).join(" · ") || undefined,
});
export const projectAssumption = (a: Assumption): SearchHit => ({
  category: "assumption",
  id: a.id,
  title: a.statement,
  subtitle: a.basis || a.owner || undefined,
});
export const projectTranscript = (t: Transcript): SearchHit => ({
  category: "transcript",
  id: t.id,
  title: t.title,
  subtitle: t.body ? snippet(t.body) : undefined,
});
export const projectUpload = (u: Upload): SearchHit => ({
  category: "upload",
  id: u.id,
  title: u.label,
});
export const projectFinding = (f: Finding): SearchHit => ({
  category: "finding",
  id: f.id,
  title: f.title,
  subtitle: `${f.severity} · ${f.domain}${f.detail ? ` · ${f.detail}` : ""}`,
});
export const projectOpportunity = (o: Opportunity): SearchHit => ({
  category: "opportunity",
  id: o.id,
  title: o.title,
  subtitle: `${o.status}${o.detail ? ` · ${o.detail}` : ""}`,
});
export const projectRecommendation = (r: Recommendation): SearchHit => ({
  category: "recommendation",
  id: r.id,
  title: r.title,
  subtitle: `${r.status}${r.body ? ` · ${snippet(r.body, 80)}` : ""}`,
});
export const projectNote = (n: FacilitationNote): SearchHit => ({
  category: "note",
  id: n.id,
  title: snippet(n.body, 80),
  subtitle: n.addedBy || undefined,
});

/** Free-form tags plus, for opportunities, the de-namespaced label so a
 *  query for "quick-win" still matches `opportunity:quick-win`. */
function tagText(tags?: string[]): string[] {
  if (!tags) return [];
  return tags.flatMap((t) => [t, stripNs(TAG_NS_OPPORTUNITY, t)]);
}

/**
 * Filter + project one category. A record matches when the query hits one
 * of its `fields` (case-insensitive substring; tags included) AND it
 * satisfies the active-tag facet (AND semantics). An empty query yields no
 * hits (the UI prompts rather than dumping everything).
 */
function runCategory<T extends { id: string; tags?: string[] }>(
  items: T[],
  q: string,
  activeTags: ReadonlyArray<string>,
  fields: (item: T) => Array<string | undefined>,
  project: (item: T) => SearchHit,
): SearchHit[] {
  return items
    .filter((item) => {
      if (!matchesTags(item.tags, activeTags)) return false;
      const parts = [...fields(item), ...tagText(item.tags)];
      return parts.some((p) => p != null && p.toLowerCase().includes(q));
    })
    .map(project);
}

export type SearchOptions = {
  /** Narrow to records carrying every one of these tags (AND). */
  activeTags?: ReadonlyArray<string>;
};

/**
 * Case-insensitive substring search across one workshop's sources. With an
 * empty query AND no active tags, returns nothing; with active tags but no
 * query, returns every record matching the tag facet (so the tag filter
 * works on its own).
 */
export function searchAll(
  query: string,
  sources: SearchSources,
  options: SearchOptions = {},
): SearchResults {
  const q = query.trim().toLowerCase();
  const activeTags = options.activeTags ?? [];
  if (!q && activeTags.length === 0) return EMPTY;

  // An empty query with active tags should match on the facet alone, so the
  // substring test must pass — `includes("")` is always true, which is
  // exactly what we want here.
  const captures = runCategory(sources.captures, q, activeTags, (c) => [c.title, c.caption, c.activity], projectCapture);
  const decisions = runCategory(sources.decisions, q, activeTags, (d) => [d.title, d.rationale, d.owner], projectDecision);
  const actions = runCategory(sources.actions, q, activeTags, (a) => [a.title, a.owner, a.due], projectAction);
  const assumptions = runCategory(sources.assumptions, q, activeTags, (a) => [a.statement, a.basis, a.owner], projectAssumption);
  const transcripts = runCategory(sources.transcripts, q, activeTags, (t) => [t.title, t.body], projectTranscript);
  const uploads = runCategory(sources.uploads, q, activeTags, (u) => [u.label], projectUpload);
  const findings = runCategory(sources.findings, q, activeTags, (f) => [f.title, f.detail, f.owner, f.domain, f.severity], projectFinding);
  const opportunities = runCategory(sources.opportunities, q, activeTags, (o) => [o.title, o.detail, o.owner, o.status], projectOpportunity);
  const recommendations = runCategory(sources.recommendations, q, activeTags, (r) => [r.title, r.body, r.owner, r.status], projectRecommendation);
  const notes = runCategory(sources.notes, q, activeTags, (n) => [n.body, n.addedBy], projectNote);

  return {
    captures,
    decisions,
    actions,
    assumptions,
    transcripts,
    uploads,
    findings,
    opportunities,
    recommendations,
    notes,
    total:
      captures.length +
      decisions.length +
      actions.length +
      assumptions.length +
      transcripts.length +
      uploads.length +
      findings.length +
      opportunities.length +
      recommendations.length +
      notes.length,
  };
}

/** Flatten a `SearchResults` into a single ordered hit list. */
export function flattenResults(r: SearchResults): SearchHit[] {
  return [
    ...r.captures,
    ...r.decisions,
    ...r.actions,
    ...r.assumptions,
    ...r.transcripts,
    ...r.uploads,
    ...r.findings,
    ...r.opportunities,
    ...r.recommendations,
    ...r.notes,
  ];
}

export type WorkshopSearchGroup = {
  workshopId: string;
  results: SearchResults;
  hits: SearchHit[];
};

export type CrossSearchResults = {
  byWorkshop: WorkshopSearchGroup[];
  /** Per-category total across all workshops (the facet counts). */
  facets: Record<SearchCategory, number>;
  total: number;
};

/**
 * Cross-workshop faceted search (CC-SEARCH). Runs {@link searchAll} per
 * workshop, keeps only the workshops with at least one hit, and tallies
 * per-category facet counts across the whole program. The single-workshop
 * `searchAll` is untouched.
 */
export function searchAllWorkshops(
  query: string,
  sourcesByWorkshop: ReadonlyArray<{ workshopId: string; sources: SearchSources }>,
  options: SearchOptions = {},
): CrossSearchResults {
  const facets = Object.fromEntries(
    SEARCH_CATEGORIES.map((c) => [c, 0]),
  ) as Record<SearchCategory, number>;
  let total = 0;
  const byWorkshop: WorkshopSearchGroup[] = [];

  for (const { workshopId, sources } of sourcesByWorkshop) {
    const results = searchAll(query, sources, options);
    if (results.total === 0) continue;
    const hits = flattenResults(results);
    for (const hit of hits) facets[hit.category] += 1;
    total += results.total;
    byWorkshop.push({ workshopId, results, hits });
  }

  return { byWorkshop, facets, total };
}
